/**
 * Group the flat `(venueId, currency)` rows of the pending-balances report
 * into one group per venue.
 *
 * The API returns one row per `(venueId, currency)`, globally ordered
 * oldest-accrual-first. `pay` settles ALL of a venue's currencies at once, so
 * the UI groups by venue (a single Pay action per venue) while keeping each
 * currency balance addressable for the per-currency Liquidate action.
 *
 * Venue order follows first appearance, which preserves the API's
 * oldest-first ordering (a venue's oldest currency row is encountered first).
 *
 * `anyPayable` is forward-prep consumed by the Pay-action commit (it gates the
 * per-venue Pay button); it's a natural property of a venue group so it lives
 * here rather than being recomputed at the call site.
 *
 * Side-effect free — unit-tested in `group-balances-by-venue.test.ts`.
 */
import type { PendingBalanceItem } from "./types";

export interface VenueBalanceGroup {
  readonly venueId: string;
  readonly venueName: string | null;
  readonly ownerDisplayName: string | null;
  /** One entry per currency, in the order the API returned them. */
  readonly balances: readonly PendingBalanceItem[];
  /** True when at least one currency is payable now (`hasConnect && payoutsEnabled`). */
  readonly anyPayable: boolean;
}

export function groupBalancesByVenue(
  items: readonly PendingBalanceItem[],
): readonly VenueBalanceGroup[] {
  const order: string[] = [];
  const byVenue = new Map<string, PendingBalanceItem[]>();

  for (const item of items) {
    const existing = byVenue.get(item.venueId);
    if (existing) {
      existing.push(item);
    } else {
      byVenue.set(item.venueId, [item]);
      order.push(item.venueId);
    }
  }

  return order.map((venueId) => {
    const balances = byVenue.get(venueId) ?? [];
    const head = balances[0];
    return {
      venueId,
      venueName: head.venueName,
      ownerDisplayName: head.ownerDisplayName,
      balances,
      anyPayable: balances.some((b) => b.hasConnect && b.payoutsEnabled),
    };
  });
}
